import { computed, effect, inject, untracked } from "@angular/core";
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { catchError, concatMap, EMPTY, pipe, switchMap, tap } from "rxjs";
import { ApplicationsClient } from "./applications.client";
import { CategoryView } from "./applications.model";
import { userContextStore } from "../../../shared/data/user-context.store";
import { ErrorNotificationService } from "../../../shared/error/error-notification.service";

interface ApplicationsState {
  categories: CategoryView[];
  onlyFavorites: boolean;
  favoriteIds: Set<string>;
  status: "idle" | "loading" | "ready" | "error";
  error: unknown;
}

const initialState: ApplicationsState = {
  categories: [],
  onlyFavorites: false,
  favoriteIds: new Set(),
  status: "idle",
  error: null,
};

export const applicationsStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed((store) => ({
    filteredCategories: computed(() => {
      const categories = store.categories();
      if (!store.onlyFavorites()) return categories;
      const favoriteIds = store.favoriteIds();
      return categories
        .map((cat) => ({
          ...cat,
          applications: cat.applications.filter((app) =>
            favoriteIds.has(app.id),
          ),
        }))
        .filter((cat) => cat.applications.length > 0);
    }),
    totalCount: computed(() =>
      store.categories().reduce((sum, cat) => sum + cat.applications.length, 0),
    ),
  })),
  withMethods(
    (
      store,
      client = inject(ApplicationsClient),
      notifier = inject(ErrorNotificationService),
    ) => {
      let confirmedFavoriteIds = new Set<string>();
      let version = 0;
      const savedFavorites = new Map<
        string,
        { favorite: boolean; version: number }
      >();
      const pendingFavorites = new Map<
        string,
        { favorite: boolean; version: number }
      >();
      const persistFavorite = rxMethod<{
        id: string;
        favorite: boolean;
        version: number;
      }>(
        pipe(
          concatMap(({ id, favorite, version: requestVersion }) =>
            (favorite
              ? client.favoriteApplication(id)
              : client.unfavoriteApplication(id)
            ).pipe(
              tap(() => {
                savedFavorites.set(id, { favorite, version: requestVersion });
                if (favorite) {
                  confirmedFavoriteIds.add(id);
                } else {
                  confirmedFavoriteIds.delete(id);
                }
                if (pendingFavorites.get(id)?.version === requestVersion) {
                  pendingFavorites.delete(id);
                }
              }),
              catchError(() => {
                if (pendingFavorites.get(id)?.version === requestVersion) {
                  pendingFavorites.delete(id);
                  const current = new Set(store.favoriteIds());
                  if (confirmedFavoriteIds.has(id)) {
                    current.add(id);
                  } else {
                    current.delete(id);
                  }
                  patchState(store, { favoriteIds: current });
                  notifier.notify(
                    "Your favourite change could not be saved. Please try again.",
                  );
                }
                return EMPTY;
              }),
            ),
          ),
        ),
      );

      return {
        load: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { status: "loading", error: null })),
            switchMap(() => {
              const versionAtLoad = version;
              const pendingAtLoad = new Set(pendingFavorites.keys());
              return client.loadApplications().pipe(
                tap({
                  next: ({ categories }) => {
                    confirmedFavoriteIds = new Set(
                      categories.flatMap((category) =>
                        category.applications
                          .filter((application) => application.favorite)
                          .map((application) => application.id),
                      ),
                    );
                    for (const [id, saved] of savedFavorites) {
                      if (
                        saved.version > versionAtLoad ||
                        pendingAtLoad.has(id)
                      ) {
                        if (saved.favorite) {
                          confirmedFavoriteIds.add(id);
                        } else {
                          confirmedFavoriteIds.delete(id);
                        }
                      }
                    }
                    const favoriteIds = new Set(confirmedFavoriteIds);
                    for (const [id, pending] of pendingFavorites) {
                      if (pending.favorite) {
                        favoriteIds.add(id);
                      } else {
                        favoriteIds.delete(id);
                      }
                    }
                    patchState(store, {
                      categories,
                      favoriteIds,
                      status: "ready",
                      error: null,
                    });
                  },
                  error: (error) =>
                    patchState(store, { status: "error", error }),
                }),
                catchError(() => EMPTY),
              );
            }),
          ),
        ),

        toggleFavorite(id: string): void {
          const current = new Set(store.favoriteIds());
          const favorite = !current.has(id);
          if (favorite) {
            current.add(id);
          } else {
            current.delete(id);
          }
          patchState(store, { favoriteIds: current });
          const pending = { favorite, version: ++version };
          pendingFavorites.set(id, pending);
          persistFavorite({ id, ...pending });
        },

        setOnlyFavorites(value: boolean): void {
          patchState(store, { onlyFavorites: value });
        },
      };
    },
  ),
  withHooks((store, userContext = inject(userContextStore)) => ({
    onInit() {
      effect(() => {
        const status = userContext.status();
        if (status === "ready" || status === "error") {
          userContext.country();
          userContext.language();
          untracked(() => store.load());
        }
      });
    },
  })),
);
